import { html, nothing } from "lit";
import type {
  MultiAgentSettingsState,
  ExecutionProfileDraft,
  MultiAgentSettingsTab,
  MultiAgentSettingsProfileTab,
} from "./types";
import { renderExecutionProfileCard } from "./execution-profile-card";
import { renderExecutionProfileForm } from "./profile-form";
import { ARCHITECTURE_TYPES } from "./types";

export type MultiAgentSettingsPanelProps = {
  state: MultiAgentSettingsState;
  onProfilesLoad: () => void;
  onProfileSeed?: () => void;
  onProfileSelect: (id: string | null) => void;
  onProfileNew: () => void;
  onProfileDelete: (id: string) => void;
  onProfileDuplicate: (id: string) => void;
  onTabChange: (tab: MultiAgentSettingsTab) => void;
  onProfileTabChange: (tab: MultiAgentSettingsProfileTab) => void;
  onDraftUpdate: (patch: Partial<ExecutionProfileDraft>) => void;
  onAgentAdd: (agentId: string) => void;
  onAgentRemove: (agentId: string) => void;
  onAgentReorder: (from: number, to: number) => void;
  onDraftSave: () => void;
  onDraftCancel: () => void;
  onStartExecution: (profileId: string, profileName: string) => void;
  // Builder state
  selectedNodeId?: string | null;
  onNodeSelect?: (id: string | null) => void;
};

const TABS: { id: MultiAgentSettingsTab; label: string }[] = [
  { id: "profiles", label: "Profiles" },
  { id: "model-providers", label: "Model Providers" },
  { id: "templates", label: "Templates" },
  { id: "policies", label: "Policies" },
];

const PROFILE_TABS: { id: MultiAgentSettingsProfileTab; label: string }[] = [
  { id: "basic", label: "Basic" },
  { id: "agents", label: "Agents" },
  { id: "model", label: "Model" },
  { id: "tools", label: "Tools" },
  { id: "limits", label: "Limits" },
  { id: "builder", label: "Builder" },
  { id: "preview", label: "Preview" },
];

export function renderMultiAgentSettingsPanel(props: MultiAgentSettingsPanelProps) {
  const { state, onTabChange } = props;

  return html`
    <div class="ma-settings">
      ${renderTabBar(state.activeTab, onTabChange)}

      ${
        state.activeTab === "profiles" && !state.draft
          ? renderProfilesList(props)
          : state.activeTab === "profiles" && state.draft
            ? renderProfileEditor(props)
            : nothing
      }

      ${state.activeTab === "model-providers" ? renderModelProvidersTab() : nothing}
      ${state.activeTab === "templates" ? renderTemplatesTab() : nothing}
      ${state.activeTab === "policies" ? renderPoliciesTab() : nothing}
    </div>
  `;
}

function renderTabBar(
  activeTab: MultiAgentSettingsTab,
  onTabChange: (tab: MultiAgentSettingsTab) => void,
) {
  return html`
    <div class="ma-tabs" role="tablist">
      ${TABS.map(
        (tab) => html`
          <button
            class="ma-tab ${activeTab === tab.id ? "ma-tab--active" : ""}"
            role="tab"
            aria-selected=${activeTab === tab.id}
            @click=${() => onTabChange(tab.id)}
          >
            ${tab.label}
          </button>
        `,
      )}
    </div>
  `;
}

function renderProfilesList(props: MultiAgentSettingsPanelProps) {
  const {
    state,
    onProfileNew,
    onProfileSelect,
    onProfileDelete,
    onProfileDuplicate,
    onStartExecution,
  } = props;

  if (state.profilesLoading) {
    return html`
      <div class="ma-loading muted">Loading profiles…</div>
    `;
  }

  if (state.profilesError) {
    return html`
      <div class="callout danger">
        Failed to load profiles: ${state.profilesError}
        <button class="btn btn--sm" @click=${props.onProfilesLoad}>Retry</button>
      </div>
    `;
  }

  return html`
    <div class="ma-profiles-list">
      <div class="ma-profiles-header row">
        <div>
          <div class="card-title">Execution Profiles</div>
          <div class="card-sub">Multi-agent execution configurations</div>
        </div>
        <button class="btn primary" @click=${onProfileNew}>
          + New Profile
        </button>
      </div>

      ${
        state.profiles.length === 0
          ? renderEmptyProfilesState({
              onNew: onProfileNew,
              onSeed: props.onProfileSeed,
            })
          : html`
            <div class="ma-profiles-grid">
              ${state.profiles.map(
                (profile) => html`
                  ${renderExecutionProfileCard({
                    profile,
                    onSelect: () => onProfileSelect(profile.id),
                    onDuplicate: () => onProfileDuplicate(profile.id),
                    onDelete: () => onProfileDelete(profile.id),
                    onRun: () => onStartExecution(profile.id, profile.name),
                    selected: state.selectedProfileId === profile.id,
                  })}
                `,
              )}
            </div>
          `
      }
    </div>
  `;
}

function renderEmptyProfilesState(props: { onNew: () => void; onSeed?: () => void }) {
  return html`
    <div class="ma-empty">
      <div class="ma-empty-icon">⬡</div>
      <div class="ma-empty-title">No execution profiles yet</div>
      <div class="ma-empty-desc">
        Create a multi-agent execution profile to define how agents
        collaborate on complex tasks.
      </div>
      <div class="row" style="gap: 8px; justify-content: center;">
        ${
          props.onSeed
            ? html`<button class="btn" @click=${props.onSeed}>Load Example Profiles</button>`
            : nothing
        }
        <button class="btn primary" @click=${props.onNew}>Create New Profile</button>
      </div>
    </div>
  `;
}

function renderProfileEditor(props: MultiAgentSettingsPanelProps) {
  const {
    state,
    onProfileTabChange,
    onDraftUpdate,
    onAgentAdd,
    onAgentRemove,
    onAgentReorder,
    onDraftSave,
    onDraftCancel,
    onStartExecution,
  } = props;

  return html`
    <div class="ma-profile-editor">
      <div class="ma-profile-editor-header row">
        <button class="btn btn--sm" @click=${() => props.onProfileSelect(null)}>
          ← Back to Profiles
        </button>
        <div style="flex: 1;">
          <div class="card-title">
            ${state.draft?.id ? "Edit Profile" : "New Profile"}
            ${state.draft?.name ? html`· ${state.draft.name}` : nothing}
          </div>
        </div>
        <div class="row" style="gap: 8px;">
          ${
            state.draft?.id && !state.draftDirty && state.draftErrors.length === 0
              ? html`<button class="btn primary" @click=${() => onStartExecution(state.draft!.id!, state.draft!.name)}>▶ Run</button>`
              : nothing
          }
          ${
            state.draftDirty
              ? html`
                <button class="btn" @click=${onDraftCancel}>Cancel</button>
                <button
                  class="btn primary"
                  ?disabled=${state.draftErrors.length > 0 || state.saving}
                  @click=${onDraftSave}
                >
                  ${state.saving ? "Saving…" : "Save Profile"}
                </button>
              `
              : nothing
          }
        </div>
      </div>

      ${
        state.draftErrors.length > 0
          ? html`
            <div class="callout danger" style="margin: 12px 0;">
              Please fix ${state.draftErrors.length} validation error(s):
              <ul>
                ${state.draftErrors.map(
                  (e) =>
                    html`<li>${e.message}${e.suggestion ? html` — <em>${e.suggestion}</em>` : nothing}</li>`,
                )}
              </ul>
            </div>
          `
          : nothing
      }

      <div class="ma-profile-tabs" role="tablist">
        ${PROFILE_TABS.map(
          (tab) => html`
            <button
              class="ma-tab ${state.activeProfileTab === tab.id ? "ma-tab--active" : ""}"
              role="tab"
              @click=${() => onProfileTabChange(tab.id)}
            >
              ${tab.label}
            </button>
          `,
        )}
      </div>

      <div class="ma-profile-content">
        ${renderExecutionProfileForm({
          draft: state.draft!,
          activeTab: state.activeProfileTab,
          onDraftUpdate,
          onAgentAdd,
          onAgentRemove,
          onAgentReorder,
          errors: state.draftErrors,
          selectedNodeId: props.selectedNodeId,
          onNodeSelect: props.onNodeSelect,
        })}
      </div>
    </div>
  `;
}

function renderModelProvidersTab() {
  return html`
    <div class="ma-section">
      <div class="card-title">Model Providers</div>
      <div class="card-sub">Configure LLM providers for multi-agent execution.</div>
      <div class="callout info" style="margin-top: 16px">
        Model provider management is configured via the main
        <strong>Model Providers</strong> section. Multi-agent profiles inherit the configured providers.
      </div>
    </div>
  `;
}

function renderTemplatesTab() {
  // These template definitions would typically come from API in a full implementation
  // For now, we show the static architecture types as template categories
  return html`
    <div class="ma-section">
      <div class="card-title">Profile Templates</div>
      <div class="card-sub">Quick-start templates for common architectures. Click "Use Template" to start with a pre-configured team.</div>

      <div class="ma-templates-section" style="margin-top: 16px;">
        <div class="card-title" style="font-size: 14px; margin-bottom: 12px;">
          MiniMax 2.7 Pre-Built Team Templates
        </div>
        <div class="ma-templates-grid">
          ${MINIMAX_TEMPLATES.map(
            (tpl) => html`
              <div class="ma-template-card">
                <div class="ma-template-header">
                  <div class="ma-template-icon">${tpl.icon}</div>
                  <div class="ma-template-category">${tpl.category}</div>
                </div>
                <div class="ma-template-name">${tpl.name}</div>
                <div class="ma-template-desc">${tpl.description}</div>
                <div class="ma-template-agents">
                  <span class="muted" style="font-size: 11px;">${tpl.agentCount} agents · ${tpl.architecture}</span>
                </div>
                <button class="btn btn--sm primary" @click=${() => handleTemplateSelect(tpl.id)}>
                  Use Template
                </button>
              </div>
            `,
          )}
        </div>
      </div>

      <div class="ma-templates-section" style="margin-top: 32px;">
        <div class="card-title" style="font-size: 14px; margin-bottom: 12px;">
          Architecture Types
        </div>
        <div class="ma-templates-grid">
          ${ARCHITECTURE_TYPES.map(
            (arch) => html`
              <div class="ma-template-card ma-template-card--arch">
                <div class="ma-template-icon">${arch.icon}</div>
                <div class="ma-template-name">${arch.label}</div>
                <div class="ma-template-desc">${arch.description}</div>
                <button class="btn btn--sm">Start from Scratch</button>
              </div>
            `,
          )}
        </div>
      </div>
    </div>
  `;
}

// MiniMax 2.7 template definitions (JAISIU-947)
const MINIMAX_TEMPLATES = [
  {
    id: "code-review-team",
    name: "Code Review Team",
    description:
      "Sequential pipeline: Architect reviews architecture → Reviewer analyzes code quality → Fixer implements improvements",
    category: "development",
    architecture: "sequential",
    icon: "→",
    agentCount: 3,
  },
  {
    id: "fullstack-development-team",
    name: "Full-Stack Development Team",
    description:
      "Hierarchical team: Lead coordinates Frontend and Backend workers, followed by QA validation",
    category: "development",
    architecture: "supervisor",
    icon: "⤵",
    agentCount: 4,
  },
  {
    id: "security-audit-team",
    name: "Security Audit Team",
    description:
      "Parallel security scanning: multiple scanners → aggregator for comprehensive vulnerability assessment",
    category: "security",
    architecture: "parallel",
    icon: "⤴",
    agentCount: 5,
  },
  {
    id: "research-team",
    name: "Research Team",
    description:
      "Supervisor-managed research: Manager assigns topics to multiple researchers, synthesizer creates final reports",
    category: "research",
    architecture: "supervisor",
    icon: "⤵",
    agentCount: 5,
  },
  {
    id: "data-pipeline-team",
    name: "Data Pipeline Team",
    description:
      "Sequential ETL pipeline: Ingest → Transform → Validate → Store with error handling",
    category: "data",
    architecture: "sequential",
    icon: "→",
    agentCount: 4,
  },
  {
    id: "customer-support-team",
    name: "Customer Support Team",
    description:
      "Hierarchical support: Supervisor manages Tier-1 specialists with escalation paths for complex issues",
    category: "support",
    architecture: "supervisor",
    icon: "⤵",
    agentCount: 5,
  },
];

// Handler for template selection (would be passed as prop in full implementation)
function handleTemplateSelect(templateId: string) {
  console.log("Template selected:", templateId);
  // In full implementation, this would:
  // 1. Call API to get full template details
  // 2. Create a draft profile from the template
  // 3. Switch to the profile editor with the draft loaded
}

function renderPoliciesTab() {
  return html`
    <div class="ma-section">
      <div class="card-title">Global Execution Policies</div>
      <div class="card-sub">Default limits and policies applied to all profiles.</div>
      <div class="callout info" style="margin-top: 16px">
        Global policies can be overridden per-profile in the
        <strong>Limits</strong> tab.
      </div>
    </div>
  `;
}
